import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { Check, Sparkles, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Paywall() {
  const [plans, setPlans] = useState(null);
  const [step, setStep] = useState({}); // {plan, stage: 'verify'|'confirming'|'done'}
  const { refresh, access, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/payment/plans").then((r) => setPlans(r.data));
  }, []);

  const openPayment = (plan) => {
    const url = plans[plan].url;
    window.open(url, "_blank", "noopener,noreferrer");
    setStep({ plan, stage: "verify" });
  };

  const confirm = async () => {
    setStep((s) => ({ ...s, stage: "confirming" }));
    try {
      await api.post("/payment/confirm", { plan: step.plan, payment_ref: null });
      await refresh();
      setStep((s) => ({ ...s, stage: "done" }));
      toast.success("Subscription activated 🎉");
      setTimeout(() => nav("/dashboard"), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm payment");
      setStep((s) => ({ ...s, stage: "verify" }));
    }
  };

  // SUCCESS SCREEN
  if (step.stage === "done") {
    return (
      <div className="max-w-xl mx-auto stagger-in">
        <div className="brimi-card p-8 md:p-10 text-center">
          <div className="w-16 h-16 grid place-items-center bg-brimi-sage/15 text-brimi-sage rounded-2xl mx-auto pulse-correct">
            <CheckCircle2 size={32} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-brimi-navyText mt-5">
            You&apos;re all set, {user?.name?.split(" ")[0] || "student"}!
          </h1>
          <p className="text-brimi-navyText/75 mt-2">
            Your <strong className="text-brimi-navyText">{step.plan === "yearly" ? "Yearly" : "Monthly"}</strong> plan
            is now active. Full access unlocked.
          </p>
          <div className="mt-5 inline-flex flex-col items-center gap-1">
            <span className="brimi-pill bg-brimi-navy text-white">★ Subscribed</span>
            {access?.paid_until && (
              <span className="text-xs text-brimi-navyText/60 mt-1">
                Valid until {new Date(access.paid_until).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="text-sm text-brimi-navyText/60 mt-6">Redirecting to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <span className="brimi-pill bg-brimi-terracotta/15 text-brimi-terracotta">
          <Sparkles size={12} /> Upgrade
        </span>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-brimi-navyText mt-3">
          {access?.is_active ? "Choose your plan" : "Your free trial has ended"}
        </h1>
        <p className="text-brimi-navyText/70 mt-2">Keep your study streak going. Cancel anytime.</p>
      </div>

      {plans && (
        <div className="grid md:grid-cols-2 gap-5">
          <PlanCard
            plan="monthly"
            title="Monthly"
            price={plans.monthly.price}
            features={["Full chapter notes", "Unlimited MCQs", "AI tutor chat", "Mock tests", "Map work"]}
            onChoose={() => openPayment("monthly")}
            active={step.plan === "monthly"}
          />
          <PlanCard
            plan="yearly"
            title="Yearly"
            price={plans.yearly.price}
            badge={plans.yearly.savings}
            features={["Everything in Monthly", "2 months free", "Priority support"]}
            onChoose={() => openPayment("yearly")}
            highlight
            active={step.plan === "yearly"}
          />
        </div>
      )}

      {(step.stage === "verify" || step.stage === "confirming") && (
        <div className="brimi-card p-6 stagger-in" data-testid="payment-verify-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-brimi-navy/10 text-brimi-navy grid place-items-center flex-shrink-0">
              <ExternalLink size={18} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg text-brimi-navyText">
                Finish payment on Razorpay
              </h3>
              <p className="text-brimi-navyText/70 text-sm mt-1">
                A new tab opened with the secure Razorpay checkout for the{" "}
                <strong>{step.plan === "yearly" ? "Yearly" : "Monthly"}</strong> plan
                {plans && (
                  <>
                    {" "}
                    (<strong>₹{plans[step.plan].price}</strong>)
                  </>
                )}
                . Complete the payment there, then come back to this tab.
              </p>
              <p className="text-brimi-navyText/70 text-sm mt-2">
                If the tab didn&apos;t open,{" "}
                <a
                  href={plans?.[step.plan]?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brimi-navy font-semibold underline"
                  data-testid="payment-reopen-link"
                >
                  click here to open it again
                </a>
                .
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={confirm}
              disabled={step.stage === "confirming"}
              className="brimi-btn-primary"
              data-testid="payment-confirm-btn"
            >
              {step.stage === "confirming" ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Activating…
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} /> I&apos;ve completed payment
                </>
              )}
            </button>
            <button
              onClick={() => setStep({})}
              disabled={step.stage === "confirming"}
              className="brimi-btn-ghost"
              data-testid="payment-cancel-btn"
            >
              Choose different plan
            </button>
          </div>
          <p className="text-xs text-brimi-navyText/55 mt-4">
            Click &ldquo;I&apos;ve completed payment&rdquo; only after your payment is successful on Razorpay. Our team
            verifies all transactions within 24 hours.
          </p>
        </div>
      )}
    </div>
  );
}

function PlanCard({ title, price, features, onChoose, highlight, badge, plan, active }) {
  return (
    <div
      className={`brimi-card p-6 relative ${
        highlight ? "border-brimi-terracotta/50 shadow-md" : ""
      } ${active ? "ring-2 ring-brimi-navy" : ""}`}
      data-testid={`plan-card-${plan}`}
    >
      {badge && <span className="brimi-pill absolute -top-3 right-4 bg-brimi-terracotta text-white">{badge}</span>}
      <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">{title}</p>
      <p className="font-heading font-bold text-brimi-navyText mt-2">
        <span className="text-4xl">₹{price}</span>
        <span className="text-base text-brimi-navyText/55"> /{plan === "monthly" ? "mo" : "yr"}</span>
      </p>
      <ul className="mt-5 space-y-2 text-sm text-brimi-navyText/85">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <Check size={14} className="text-brimi-sage" /> {f}
          </li>
        ))}
      </ul>
      <button
        onClick={onChoose}
        className={`mt-6 w-full ${highlight ? "brimi-btn-primary" : "brimi-btn-ghost border border-brimi-creamSoft"}`}
        data-testid={`plan-choose-${plan}`}
      >
        Continue to Razorpay
      </button>
    </div>
  );
}
